import { useEffect, useMemo, useState } from "react";
import { Icon } from "../Common.jsx";
import { listTeam } from "../../lib/api";

// Department-grouped, searchable, role-filterable multi-select of company
// members (managers + employees). Controlled: parent owns `selected` (a Set of
// userIds) and receives the next Set via `onChange`. Used by the project member
// picker and the announcement recipient picker.
export default function MemberPicker({ selected, onChange }) {
  const [departments, setDepartments] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [expandedDepts, setExpandedDepts] = useState(() => new Set());

  useEffect(() => {
    listTeam({ scope: "company", groupBy: "department" })
      .then((d) => setDepartments(d.departments || []))
      .catch(() => {});
  }, []);

  const toggle = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    onChange(n);
  };

  const query = memberSearch.trim().toLowerCase();

  const filteredDepts = useMemo(() => {
    return departments
      .map((dept) => {
        let users = dept.users;
        if (query) users = users.filter((u) => u.name.toLowerCase().includes(query));
        if (roleFilter === "manager") users = users.filter((u) => u.role === "manager");
        else if (roleFilter === "employee") users = users.filter((u) => u.role === "employee");
        return { ...dept, users };
      })
      .filter((d) => d.users.length > 0);
  }, [departments, query, roleFilter]);

  const allManagerIds = useMemo(() => {
    const ids = [];
    for (const d of departments) for (const u of d.users) if (u.role === "manager") ids.push(u.id);
    return ids;
  }, [departments]);

  const allEmployeeIds = useMemo(() => {
    const ids = [];
    for (const d of departments) for (const u of d.users) if (u.role === "employee") ids.push(u.id);
    return ids;
  }, [departments]);

  const allManagersSelected = allManagerIds.length > 0 && allManagerIds.every((id) => selected.has(id));
  const allEmployeesSelected = allEmployeeIds.length > 0 && allEmployeeIds.every((id) => selected.has(id));

  function toggleAllManagers() {
    const next = new Set(selected);
    if (allManagersSelected) allManagerIds.forEach((id) => next.delete(id));
    else allManagerIds.forEach((id) => next.add(id));
    onChange(next);
  }

  function toggleAllEmployees() {
    const next = new Set(selected);
    if (allEmployeesSelected) allEmployeeIds.forEach((id) => next.delete(id));
    else allEmployeeIds.forEach((id) => next.add(id));
    onChange(next);
  }

  function toggleDept(dept) {
    const next = new Set(selected);
    const allIn = dept.users.every((u) => selected.has(u.id));
    dept.users.forEach((u) => (allIn ? next.delete(u.id) : next.add(u.id)));
    onChange(next);
  }

  function toggleDeptExpand(name) {
    setExpandedDepts((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  const { fullDepts, remainingSelected } = useMemo(() => {
    const full = departments.filter(
      (d) => d.users.length > 0 && d.users.every((u) => selected.has(u.id)),
    );
    const fullIds = new Set(full.flatMap((d) => d.users.map((u) => u.id)));
    return { fullDepts: full, remainingSelected: [...selected].filter((id) => !fullIds.has(id)) };
  }, [departments, selected]);

  return (
    <>
      <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <div className="toolbar-search" style={{ flex: 1, minWidth: 160 }}>
          <Icon name="search" size={14} />
          <input
            type="text"
            placeholder="Search people…"
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
          />
        </div>
        <div className="filter-chips" style={{ display: "flex", gap: 4 }}>
          {["all", "manager", "employee"].map((r) => (
            <button
              key={r}
              className={`btn btn-sm ${roleFilter === r ? "btn-primary" : "btn-ghost"}`}
              onClick={() => setRoleFilter(r)}
              style={{ textTransform: "capitalize", fontSize: 12, padding: "2px 10px" }}
            >
              {r === "all" ? "All" : r + "s"}
            </button>
          ))}
        </div>
      </div>
      <div
        style={{
          maxHeight: 260,
          overflowY: "auto",
          border: "1px solid var(--border)",
          borderRadius: 8,
        }}
      >
        {filteredDepts.length === 0 ? (
          <div style={{ padding: 12, fontSize: 13, color: "var(--text-muted)" }}>No people found.</div>
        ) : (
          filteredDepts.map((dept) => {
            const deptSelected = dept.users.filter((u) => selected.has(u.id));
            const allDeptSelected = deptSelected.length === dept.users.length;
            const someDeptSelected = deptSelected.length > 0 && deptSelected.length < dept.users.length;
            const expanded = expandedDepts.has(dept.name);
            return (
              <div key={dept.name} style={{ borderBottom: "1px solid var(--border)" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    cursor: "pointer",
                    background:
                      someDeptSelected || allDeptSelected
                        ? "var(--bg-subtle, rgba(99,102,241,0.04))"
                        : "transparent",
                  }}
                  onClick={() => toggleDeptExpand(dept.name)}
                >
                  <Icon name={expanded ? "chevron-down" : "chevron-right"} size={14} />
                  <input
                    type="checkbox"
                    checked={allDeptSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someDeptSelected;
                    }}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleDept(dept);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span style={{ flex: 1, fontWeight: 500, fontSize: 14 }}>{dept.name}</span>
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{dept.count} people</span>
                  {deptSelected.length > 0 && (
                    <span style={{ fontSize: 11, color: "var(--primary)", fontWeight: 600 }}>
                      {deptSelected.length} selected
                    </span>
                  )}
                </div>
                {expanded &&
                  dept.users.map((u) => (
                    <label
                      key={u.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "6px 12px 6px 48px",
                        cursor: "pointer",
                        fontSize: 13,
                        background: selected.has(u.id)
                          ? "var(--bg-subtle, rgba(99,102,241,0.04))"
                          : "transparent",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(u.id)}
                        onChange={() => toggle(u.id)}
                      />
                      <span style={{ flex: 1 }}>{u.name}</span>
                      {u.role === "manager" && (
                        <span
                          className="badge"
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 0.4,
                            background: "rgba(99,102,241,0.12)",
                            color: "var(--primary, #6366f1)",
                            border: "1px solid rgba(99,102,241,0.3)",
                          }}
                        >
                          Manager
                        </span>
                      )}
                      <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        {u.department || "—"}
                      </span>
                    </label>
                  ))}
              </div>
            );
          })
        )}
      </div>
      {departments.length > 0 && (
        <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}>
            <input type="checkbox" checked={allManagersSelected} onChange={toggleAllManagers} />
            All Managers ({allManagerIds.length})
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}>
            <input type="checkbox" checked={allEmployeesSelected} onChange={toggleAllEmployees} />
            All Employees ({allEmployeeIds.length})
          </label>
        </div>
      )}
      {selected.size > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
          {fullDepts.map((d) => (
            <span
              key={d.name}
              className="badge"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "3px 8px",
                background: "rgba(99,102,241,0.1)",
                color: "var(--primary)",
                borderRadius: 6,
                fontSize: 12,
              }}
            >
              {d.name} ({d.count})
              <button
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "inherit", fontSize: 14, lineHeight: 1 }}
                onClick={() => toggleDept(d)}
              >
                ×
              </button>
            </span>
          ))}
          {remainingSelected.length > 0 && (
            <span
              className="badge"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "3px 8px",
                background: "var(--bg-subtle)",
                color: "var(--text-muted)",
                borderRadius: 6,
                fontSize: 12,
              }}
            >
              +{remainingSelected.length} individual
              {remainingSelected.length !== 1 ? "s" : ""}
              <button
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "inherit", fontSize: 14, lineHeight: 1 }}
                onClick={() => onChange(new Set())}
              >
                ×
              </button>
            </span>
          )}
        </div>
      )}
    </>
  );
}
